/**
 * Shared SVG mask helpers for text-shaped clips (used by text-bg-video and banner knock-out).
 */

/**
 * Build an SVG data-URI mask with one <text> element per visual line (white glyphs on transparent).
 */
export function buildMaskDataUri(segments, w, h, style) {
  if (w <= 0 || h <= 0 || !segments.length) return null;

  const family = (style.fontFamily || "sans-serif").replace(/"/g, "'");

  const textEls = segments.map((seg) => {
    const safe = seg.text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

    return [
      `<text`,
      ` x="${seg.x.toFixed(2)}"`,
      ` y="${seg.baselineY.toFixed(2)}"`,
      ` font-family="${family}"`,
      ` font-size="${style.fontSize}"`,
      ` font-weight="${style.fontWeight}"`,
      ` font-style="${style.fontStyle}"`,
      ` letter-spacing="${style.letterSpacing}"`,
      ` fill="white"`,
      ` dominant-baseline="alphabetic"`,
      `>${safe}</text>`,
    ].join("");
  });

  const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}">${textEls.join("")}</svg>`;
  return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}")`;
}

export function makeLineSegment(textNode, fullText, start, end, mediaRect) {
  const r = document.createRange();
  r.setStart(textNode, start);
  r.setEnd(textNode, end);
  const rect = r.getBoundingClientRect();
  return {
    text: fullText.slice(start, end).trimEnd(),
    x: rect.left - mediaRect.left,
    baselineY: rect.top - mediaRect.top + rect.height * 0.82,
  };
}

/**
 * Walk character-by-character using Range to detect visual line breaks.
 * Returns one segment per rendered line with x position and baseline y,
 * both expressed relative to the media element's top-left corner.
 */
export function getLineSegments(textSpan, mediaRect, fontSizePx) {
  const textNode = textSpan.firstChild;
  if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return [];

  const fullText = textNode.textContent;
  if (!fullText) return [];

  const lineThreshold = fontSizePx * 0.5;
  const segments = [];
  let segStart = 0;
  let segTop = null;

  for (let i = 0; i < fullText.length; i++) {
    const r = document.createRange();
    r.setStart(textNode, i);
    r.setEnd(textNode, i + 1);
    const charRect = r.getBoundingClientRect();

    if (charRect.width === 0 && charRect.height === 0) continue;

    if (segTop === null) {
      segTop = charRect.top;
      segStart = i;
    } else if (charRect.top > segTop + lineThreshold) {
      segments.push(makeLineSegment(textNode, fullText, segStart, i, mediaRect));
      segTop = charRect.top;
      segStart = i;
    }
  }

  if (segTop !== null) {
    segments.push(makeLineSegment(textNode, fullText, segStart, fullText.length, mediaRect));
  }

  return segments;
}

export function applyDataUriMask(el, dataUri) {
  if (!el || !dataUri) return;
  el.style.setProperty("-webkit-mask-image", dataUri);
  el.style.setProperty("mask-image", dataUri);
  el.style.setProperty("-webkit-mask-repeat", "no-repeat");
  el.style.setProperty("mask-repeat", "no-repeat");
  el.style.setProperty("-webkit-mask-position", "0 0");
  el.style.setProperty("mask-position", "0 0");
  el.style.setProperty("-webkit-mask-size", "100% 100%");
  el.style.setProperty("mask-size", "100% 100%");
}

const FULL_WHITE_MASK = "linear-gradient(#ffffff, #ffffff)";

/**
 * White field with glyph-shaped holes: full-white mask minus white-on-transparent text mask.
 * (Single SVG white+black often composites to “all white” in engines; two layers are reliable.)
 */
export function applyBannerSubtractKnockoutMask(el, segments, w, h, style) {
  if (!el || w <= 0 || h <= 0 || !segments.length) return;

  const textMask = buildMaskDataUri(segments, w, h, style);
  if (!textMask) return;

  const combined = `${FULL_WHITE_MASK}, ${textMask}`;

  el.style.setProperty("-webkit-mask-image", combined);
  el.style.setProperty("-webkit-mask-repeat", "no-repeat, no-repeat");
  el.style.setProperty("-webkit-mask-size", "100% 100%, 100% 100%");
  el.style.setProperty("-webkit-mask-position", "0 0, 0 0");
  el.style.setProperty("-webkit-mask-composite", "destination-out");

  el.style.setProperty("mask-image", combined);
  el.style.setProperty("mask-repeat", "no-repeat, no-repeat");
  el.style.setProperty("mask-size", "100% 100%, 100% 100%");
  el.style.setProperty("mask-position", "0 0, 0 0");
  el.style.setProperty("mask-composite", "subtract");
}

/** Safari often reports 0×0 until the first layout pass; poll until we get a real size. */
export function whenHasLayout(media, fn, maxAttempts = 30) {
  let n = 0;
  const check = () => {
    n += 1;
    if ((media.clientWidth > 0 && media.clientHeight > 0) || n >= maxAttempts) {
      fn();
    } else {
      requestAnimationFrame(check);
    }
  };
  requestAnimationFrame(check);
}
